import { createElement } from "react"
import { toast } from "sonner"

import i18n from "@/lib/i18n/client"
import type { ToastVariant } from "@/types"

/**
 * Options for {@link flash}.
 */
export interface FlashOptions {
  /**
   * Sonner chrome variant. Defaults to `"neutral"` (bare `toast()` with
   * no icon — matches the historical `flash[:notice]` appearance).
   */
  variant?: ToastVariant
  /**
   * Pre-resolved copy to display. Provided by the server-driven admin
   * path (`FlashData.toast.message`) so `admin.*.flash.*` copy never needs
   * to live in the FE i18n bundle. When omitted, the message is resolved
   * client-side via `i18n.t(key)` — for client-side callers whose key DOES
   * exist in the FE bundle.
   */
  message?: string
  /**
   * Optional Sonner toast id (dedup / update target). Omitted by the
   * admin path so stacking behaviour is identical to today.
   */
  id?: string | number
}

const VARIANT_FALLBACK: ToastVariant = "neutral"

/**
 * Surface a Sonner toast that self-identifies via a `data-toast-key` DOM
 * attribute equal to its i18n `key` — a stable, locale-agnostic handle
 * for Playwright (`[data-toast-key="admin.blocklist.flash.blocked"]`)
 * that survives copy edits and EN/FR locale switches.
 *
 * The visible toast (message, variant chrome, icon, position, timing,
 * stacking, `aria-live`) is identical to a raw `toast(...)` call — the
 * ONLY delta is the added attribute (OKS-113 / UX Spec). The attribute is
 * landed by rendering the message inside a `<span data-toast-key={key}>`
 * title node; deliberately NOT `toast.custom` / `toastOptions` (which would
 * rebuild or globalise the chrome and break visual parity). The module
 * stays `.ts` (no JSX) by using `createElement`.
 *
 * @param key  - the i18n key; also the verbatim `data-toast-key` value.
 *               Always the BARE key (never interpolated), so the test
 *               handle never leaks `%{brand}`/`%{email}` values.
 * @param opts - see {@link FlashOptions}. Server-driven callers pass the
 *               BE-resolved `message`; client-side callers omit it to
 *               resolve `i18n.t(key)` from the FE bundle.
 *
 * @example
 * // client-side caller (key exists in the FE bundle)
 * flash("missions.flash.saved", { variant: "success" })
 * @example
 * // server-driven (via useFlash → FlashData.toast)
 * flash(payload.key, { message: payload.message, variant: payload.variant })
 */
export function flash(key: string, opts: FlashOptions = {}): void {
  const { variant = VARIANT_FALLBACK, message, id } = opts
  const text = message ?? i18n.t(key)
  // Render the message inside a queryable span so the toast carries
  // `data-toast-key` INSIDE `[data-sonner-toast]` while keeping Sonner's
  // native chrome (icon / variant colour / position). `createElement`
  // avoids JSX, keeping this a `.ts` module per the ticket.
  const node = createElement("span", { "data-toast-key": key }, text)
  const options = id === undefined ? undefined : { id }

  switch (variant) {
    case "success":
      toast.success(node, options)
      break
    case "error":
      toast.error(node, options)
      break
    case "warning":
      toast.warning(node, options)
      break
    default:
      toast(node, options)
  }
}
